import { useCallback } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAgentManagementClient } from "src/agents/queries/management";
import {
  AgentVersionStatus,
  AgentVersionsDependingOnFlowResponseItem,
} from "src/clients/agent-management";
import { useIsAgentsEnabled } from "src/hooks/useIsAgentsEnabled";

const AGENT_DEPS_KEY = "agent-dependencies-on-flow";

type AgentDepsOptions = {
  enabled?: boolean;
};

type AgentManagementClient = NonNullable<
  ReturnType<typeof useAgentManagementClient>["client"]
>;

// --- Shared query key & fetch logic ---

const agentDepsQueryKey = (
  level: "flow" | "version",
  status: AgentVersionStatus,
  flowSlug: string | undefined,
  flowVersionId: string | undefined,
) => [AGENT_DEPS_KEY, level, status, flowSlug, flowVersionId] as const;

const fetchAgentDeps = async (
  client: AgentManagementClient,
  level: "flow" | "version",
  status: AgentVersionStatus,
  flowSlug: string,
  flowVersionId?: string,
): Promise<AgentVersionsDependingOnFlowResponseItem[]> => {
  if (level === "flow") {
    const response =
      await client.getAgentsDependingOnFlowAgentsManagementApiV0DependingOnFlowFlowSlugGet(
        { flowSlug, status },
      );
    return response.data.agent_versions;
  }

  if (!flowVersionId) throw new Error("unreachable: guarded by caller");
  const response =
    await client.getAgentsDependingOnFlowVersionAgentsManagementApiV0DependingOnFlowFlowSlugVersionFlowVersionIdGet(
      { flowSlug, flowVersionId, status },
    );
  return response.data.agent_versions;
};

// --- Internal factory ---

const useAgentDepsQuery = (
  level: "flow" | "version",
  status: AgentVersionStatus,
  flowSlug: string | undefined,
  flowVersionId: string | undefined,
  options?: AgentDepsOptions,
) => {
  const { client } = useAgentManagementClient();
  const { isEnabled: isAgentsEnabled } = useIsAgentsEnabled();

  const hasRequiredParams =
    level === "flow" ? !!flowSlug : !!flowSlug && !!flowVersionId;
  const shouldFetch =
    (options?.enabled ?? true) && hasRequiredParams && isAgentsEnabled;

  const query = useQuery({
    queryKey: agentDepsQueryKey(level, status, flowSlug, flowVersionId),
    queryFn: () => {
      if (!client || !flowSlug)
        throw new Error("unreachable: guarded by enabled");
      return fetchAgentDeps(client, level, status, flowSlug, flowVersionId);
    },
    enabled: shouldFetch && !!client,
  });

  return {
    ...query,
    data: isAgentsEnabled ? query.data : [],
    isLoading: isAgentsEnabled ? query.isPending || query.isFetching : false,
  };
};

// --- Flow-level hooks (all versions of a flow) ---

export const usePublishedAgentsDependingOnFlow = (
  flowSlug: string | undefined,
  options?: AgentDepsOptions,
) =>
  useAgentDepsQuery(
    "flow",
    AgentVersionStatus.PUBLISHED,
    flowSlug,
    undefined,
    options,
  );

export const useDraftAgentsDependingOnFlow = (
  flowSlug: string | undefined,
  options?: AgentDepsOptions,
) =>
  useAgentDepsQuery(
    "flow",
    AgentVersionStatus.DRAFT,
    flowSlug,
    undefined,
    options,
  );

// --- Version-level hooks (specific flow version) ---

export const usePublishedAgentsDependingOnFlowVersion = (
  flowSlug: string,
  flowVersionId: string | undefined,
  options?: AgentDepsOptions,
) =>
  useAgentDepsQuery(
    "version",
    AgentVersionStatus.PUBLISHED,
    flowSlug,
    flowVersionId,
    options,
  );

export const useDraftAgentsDependingOnFlowVersion = (
  flowSlug: string,
  flowVersionId: string | undefined,
  options?: AgentDepsOptions,
) =>
  useAgentDepsQuery(
    "version",
    AgentVersionStatus.DRAFT,
    flowSlug,
    flowVersionId,
    options,
  );

// --- Prefetch hooks ---

export const usePrefetchAgentDepsForFlow = () => {
  const queryClient = useQueryClient();
  const { client } = useAgentManagementClient();
  const { isEnabled: isAgentsEnabled } = useIsAgentsEnabled();

  return useCallback(
    (flowSlug: string) => {
      if (!client || !isAgentsEnabled) return;
      for (const status of [
        AgentVersionStatus.PUBLISHED,
        AgentVersionStatus.DRAFT,
      ] as const) {
        queryClient.prefetchQuery({
          queryKey: agentDepsQueryKey("flow", status, flowSlug, undefined),
          queryFn: () => fetchAgentDeps(client, "flow", status, flowSlug),
        });
      }
    },
    [queryClient, client, isAgentsEnabled],
  );
};

export const usePrefetchAgentDepsForFlowVersion = () => {
  const queryClient = useQueryClient();
  const { client } = useAgentManagementClient();
  const { isEnabled: isAgentsEnabled } = useIsAgentsEnabled();

  return useCallback(
    (flowSlug: string, flowVersionId: string) => {
      if (!client || !isAgentsEnabled) return;
      for (const status of [
        AgentVersionStatus.PUBLISHED,
        AgentVersionStatus.DRAFT,
      ] as const) {
        queryClient.prefetchQuery({
          queryKey: agentDepsQueryKey(
            "version",
            status,
            flowSlug,
            flowVersionId,
          ),
          queryFn: () =>
            fetchAgentDeps(client, "version", status, flowSlug, flowVersionId),
        });
      }
    },
    [queryClient, client, isAgentsEnabled],
  );
};
